/**
 * markbird shared types.
 *
 * These types are B-ready: they model document ownership and share permissions
 * NOW, even though the MVP (path A) only ever exercises the anonymous,
 * read-only path. Enabling accounts (phase B) must be additive — see the
 * B-readiness checklist in the root README.
 */
import { parseFrontmatter, stripFrontmatter, readList } from './frontmatter.js';

export * from './frontmatter.js';
export * from './text-stats.js';

/** A share grants either read-only or edit access. MVP only ever issues 'read'. */
export type Permission = 'read' | 'edit';

/**
 * Owner of a document / share.
 * - `null` = anonymous (MVP path A).
 * - later (B) = an authenticated user id (e.g. Firebase Auth `uid`).
 *
 * The field is present in the schema now so B is a flag flip, not a migration.
 */
export type OwnerId = string | null;

/** View modes for the editor surface. */
export type ViewMode = 'split' | 'switch';

/** Which pane is active in `switch` view mode. */
export type SwitchPane = 'source' | 'preview';

/** Theme preference. `system` follows `prefers-color-scheme`. */
export type ThemePreference = 'light' | 'dark' | 'system';

/** Export formats produced entirely client-side in the MVP. */
export type ExportFormat = 'md' | 'html' | 'pdf' | 'docx' | 'slides';

/**
 * A markdown document. Lives in IndexedDB locally (the library) and, when
 * shared, is snapshotted to Firestore `documents/{id}`.
 */
export interface MarkdownDocument {
  /** nanoid. */
  id: string;
  /** Derived from the first H1 or filename; user-editable. */
  title: string;
  /** Raw markdown source. */
  content: string;
  createdAt: number;
  updatedAt: number;
  /** MVP: always `null`. B: the authenticated owner's id. Present now. */
  ownerId: OwnerId;
  /** Soft-delete timestamp; `null`/absent = live. Set when moved to trash. */
  deletedAt?: number | null;
  /** Pinned to the top of the library; `undefined`/false = normal. */
  pinned?: boolean;
  /** Organizational labels (tags) — used to group/filter the library. */
  tags?: string[];
  /**
   * Alternative titles this document answers to, so `[[Other name]]` resolves
   * here. Mirrors frontmatter `aliases`; the file stays the source of truth.
   */
  aliases?: string[];
  /**
   * Folder path (slash-separated, e.g. `Work/Projects`). `null`/absent = root.
   * A lightweight tree: folders exist implicitly wherever a document points to
   * them, so there is no separate folders collection.
   */
  folder?: string | null;
  /**
   * Position in the manual library order: a fractional rank, compared as a
   * string. `null`/absent = not placed yet (listed first, newest first).
   */
  rank?: string | null;
  /**
   * When pinned/folder/rank last changed. Placement has its own clock because it
   * must not bump `updatedAt`, and sync needs a clock to tell which placement is newer.
   */
  metaUpdatedAt?: number;
}

/**
 * A share record. Written to Firestore `shares/{token}` when a document is
 * shared. The token is the primary key and the capability — knowing it grants
 * the `permission` it carries.
 */
export interface Share {
  /** Unguessable, >=128-bit capability token (nanoid, 22+ chars). */
  token: string;
  docId: string;
  /** MVP: always 'read'. */
  permission: Permission;
  /** MVP: always `null`. B: the sharer's id. */
  createdBy: OwnerId;
  createdAt: number;
  /** Optional TTL. When set, a Firestore TTL policy on this field expires it. */
  expiresAt: number | null;
  revoked: boolean;
  /** Server-only: salted password hash if the share is password-protected. Never sent to clients. */
  passwordHash?: string;
  /**
   * Server-only: hash of the deletion secret handed to whoever created the
   * share. It is what lets someone without an account delete their own share —
   * so the server must be unable to hand it back out.
   */
  deleteHash?: string;
  /** Optional human-friendly published slug, resolvable at `/p/<slug>`. */
  slug?: string;
}

/** Valid publish slug: 3–64 chars, lowercase alphanumerics and single hyphens. */
export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,62})[a-z0-9]$/;

/** True if `s` is a well-formed publish slug (no leading/trailing/double hyphen). */
export function isValidSlug(s: string): boolean {
  return SLUG_RE.test(s) && !s.includes('--');
}

/**
 * A published site: several notes as one small linked website.
 *
 * A site is not a new kind of share — it is a **group** of them. Each page is
 * an ordinary share with its own token and snapshot, so everything that
 * already exists for a shared note (expiry and its TTL, reporting, takedown,
 * deletion removing the server-side copy) keeps working per page, and the site
 * record only holds what a group needs: its name, its address and its order.
 */
export interface Site {
  id: string;
  /** The published address: `/p/<slug>`, and `/p/<slug>/<page>` per page. */
  slug: string;
  title: string;
  createdBy: OwnerId;
  createdAt: number;
  expiresAt: number | null;
  pages: SitePage[];
  /** Server-only: hash of the deletion secret handed out once at creation. */
  deleteHash?: string;
}

export interface SitePage {
  /** Address within the site, unique among its pages. */
  slug: string;
  title: string;
  /** The share this page is: readable on its own at `/s/<token>`. */
  token: string;
}

/** Body of `POST /api/sites`. */
export interface CreateSiteRequest {
  title: string;
  slug: string;
  pages: { title: string; content: string }[];
  expiresAt?: number | null;
}

/** Response of `POST /api/sites`. */
export interface CreateSiteResponse {
  id: string;
  url: string;
  expiresAt: number | null;
  /** Returned once and never again: the secret that deletes the whole site. */
  deleteToken: string;
  deleteUrl: string;
}

/**
 * Response of `GET /api/sites/by-slug/:slug[/:page]` — one page, and the whole
 * table of contents with it, because a page of a site is never read without
 * the way to the others.
 */
export interface GetSiteResponse {
  id: string;
  siteTitle: string;
  slug: string;
  pages: { slug: string; title: string }[];
  /**
   * `token` is the page's own share token. It is not a secret — the page is
   * published — and it is what lets a reader report this page and what a
   * `/s/<token>` link to it uses.
   */
  page: { slug: string; title: string; content: string; token: string };
}

/** How many pages one site may have, and how much a site may weigh in total. */
export const MAX_SITE_PAGES = 100;
export const MAX_SITE_BYTES = 8 * 1024 * 1024;

/** Per-doc UI session state, persisted locally so reload restores exactly. */
export interface DocSessionState {
  docId: string;
  /** CodeMirror selection anchor/head as absolute offsets. */
  cursor: { anchor: number; head: number };
  /** Editor scroll top in px. */
  scrollTop: number;
  viewMode: ViewMode;
  switchPane: SwitchPane;
}

/* ------------------------------------------------------------------ */
/* API request/response contracts (same-origin /api/**).              */
/* ------------------------------------------------------------------ */

/** Body of `POST /api/shares`. Raw markdown; server does not sanitize. */
export interface CreateShareRequest {
  title: string;
  content: string;
  /**
   * B-ready: the client may request an edit share and attribute an owner.
   * The MVP server ignores/forces these to the read+anonymous defaults, but
   * the field exists so nothing is hard-coded to `'read'` in a blocking way.
   */
  permission?: Permission;
  expiresAt?: number | null;
  /** Optional password to protect the share (signed-in edit shares or any read share). */
  password?: string;
  /** Optional custom publish slug (resolvable at `/p/<slug>`). */
  slug?: string;
}

/** Response of `POST /api/shares`. */
export interface CreateShareResponse {
  token: string;
  url: string;
  /** When the link stops working. Anonymous shares always have one; see `ANON_SHARE_MAX_MS`. */
  expiresAt: number | null;
  /**
   * The secret that deletes this share, returned once and never again. Kept by
   * the creator (markbird remembers it on the device that made the link).
   */
  deleteToken: string;
  /** A link that deletes the share: the share URL with the secret in its fragment. */
  deleteUrl: string;
}

/** Response of `GET /api/shares/:token` (and `…/by-slug/:slug`, which adds `token`). */
export interface GetShareResponse {
  title: string;
  content: string;
  permission: Permission;
  /** The canonical share token — included when resolving by slug. */
  token?: string;
}

/** One row in a signed-in user's share list (`GET /api/shares`). */
export interface ShareSummary {
  token: string;
  url: string;
  title: string;
  permission: Permission;
  createdAt: number;
  expiresAt?: number | null;
  /** Whether the share requires a password to open. */
  protected?: boolean;
}

/** Response of `GET /api/shares` (the caller's own shares, newest first). */
export interface ListSharesResponse {
  shares: ShareSummary[];
}

/** Body of `PUT /api/shares/:token` — write back to an editable share. */
export interface UpdateShareRequest {
  title: string;
  content: string;
}

/** Body of `PATCH /api/shares/:token` — change a share's expiry (owner only). */
export interface UpdateShareExpiryRequest {
  /** New expiry as epoch ms, or `null` to never expire. */
  expiresAt: number | null;
}

/** Why a shared document was reported. */
export const REPORT_REASONS = ['illegal', 'copyright', 'personal_data', 'abuse', 'other'] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

/** Upper bounds for the free-text parts of a report. */
export const REPORT_DETAILS_MAX = 2000;
export const REPORT_CONTACT_MAX = 254;

/**
 * Body of `POST /api/reports`. Exactly one of `token` / `slug` identifies the
 * share — a published `/p/:slug` page is reported by what its visitor can see.
 */
export interface CreateReportRequest {
  token?: string;
  slug?: string;
  reason: ReportReason;
  details?: string;
  /** Optional address for follow-up. Never required to report. */
  contact?: string;
}

export type ReportStatus = 'open' | 'removed' | 'dismissed';

/** A report as the operator sees it in `GET /api/admin/reports`. */
export interface AdminReport {
  id: string;
  reason: ReportReason;
  details: string | null;
  contact: string | null;
  createdAt: number;
  status: ReportStatus;
  share: {
    token: string;
    url: string;
    /** `null` once the share is gone — reported content may already be deleted. */
    title: string | null;
  };
}

export interface ListReportsResponse {
  reports: AdminReport[];
}

/** Standard API error envelope. */
export interface ApiError {
  error: string;
  message?: string;
}

/** Maximum accepted markdown payload size for a share (bytes). ~1MB. */
export const MAX_SHARE_BYTES = 1_048_576;

/** Length of generated share tokens (nanoid). 22 chars ≈ 131 bits. */
export const SHARE_TOKEN_LENGTH = 24;

/**
 * Anonymous shares expire, because nothing else can ever remove them: revoking
 * from the share list needs an account. The creator also gets a deletion
 * secret, so they need not wait for the expiry — but the expiry is the
 * guarantee, since a secret can be lost.
 */
export const ANON_SHARE_DEFAULT_MS = 30 * 24 * 60 * 60 * 1000;
export const ANON_SHARE_MAX_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Derive a document title: explicit frontmatter `title`, else the first H1,
 * else the first non-empty line. Frontmatter is skipped for the last two so a
 * note that opens with `---` is not titled after its own fence.
 */
export function deriveTitle(content: string, fallback = 'Untitled'): string {
  const { data, body } = parseFrontmatter(content);
  const explicit = data.title;
  if (typeof explicit === 'string' && explicit.trim()) return explicit.trim().slice(0, 200);

  for (const rawLine of body.split('\n')) {
    const line = rawLine.trim();
    const h1 = /^#\s+(.+?)\s*#*\s*$/.exec(line);
    if (h1 && h1[1]) return h1[1].trim().slice(0, 200);
  }
  // Otherwise use the first non-empty line, stripped of markdown noise.
  for (const rawLine of body.split('\n')) {
    const line = rawLine.trim();
    if (line) return line.replace(/^#+\s*/, '').slice(0, 200);
  }
  return fallback;
}

/**
 * Tags declared in a document's own frontmatter. These belong to the file, so
 * they survive a round-trip through disk or another editor — unlike anything
 * held only in the local database.
 */
export function frontmatterTags(content: string): string[] {
  const { data } = parseFrontmatter(content);
  // `tag` is accepted as an alias because other tools emit it.
  return [...readList(data, 'tags'), ...readList(data, 'tag')];
}

/** Alternative titles a `[[wikilink]]` may resolve to. */
export function frontmatterAliases(content: string): string[] {
  const { data } = parseFrontmatter(content);
  return [...readList(data, 'aliases'), ...readList(data, 'alias')];
}

/** Body text with frontmatter removed — what search, counts and excerpts see. */
export { stripFrontmatter as documentBody };
